import styled, { CSSProperties, css } from 'styled-components'
import { Colors, Intent } from '@blueprintjs/core'

import quarks from './quarks'
import makeExcludePropsConfig from './make-exclude-props-config'

type SpacingKeys = keyof typeof quarks['margins']
type BorderKeys = keyof typeof quarks['borderRadii']

const gridFractions = ['0', '1', '1/2', '1/4', '3/4', '1/3', '2/3'] as const
type GridFraction = typeof gridFractions[number]

export type Spacing = SpacingKeys | GridFraction | 'auto' | 'none' | number
type Alignment = 'auto' | 'baseline' | 'center' | 'end' | 'start'
type Justification =
  | 'start'
  | 'end'
  | 'center'
  | 'between'
  | 'around'
  | 'evenly'
export type FlexDirection = 'row' | 'col' | 'row-reverse' | 'col-reverse'
type FlexWrap = boolean | 'nowrap' | 'reverse'
type Color = keyof typeof Colors | Intent
type BorderRadius = BorderKeys

export type BoxProps = {
  className?: string
  m?: Spacing
  mx?: Spacing
  my?: Spacing
  mt?: Spacing
  mb?: Spacing
  ml?: Spacing
  mr?: Spacing
  p?: Spacing
  px?: Spacing
  py?: Spacing
  pt?: Spacing
  pb?: Spacing
  pl?: Spacing
  pr?: Spacing

  width?: Spacing
  minWidth?: Spacing
  maxWidth?: Spacing

  height?: Spacing
  minHeight?: Spacing
  maxHeight?: Spacing

  flex?: string
  items?: Alignment
  justify?: Justification
  flexDir?: FlexDirection
  wrap?: FlexWrap

  // inline-block or inline-flex
  inline?: boolean

  bg?: Color
  color?: Color

  borderRadius?: BorderRadius
  textAlign?: CSSProperties['textAlign']
  gap?: Spacing
}

const isGridFraction = (key: unknown): key is GridFraction => {
  return gridFractions.includes(key as GridFraction)
}

const resolveGridFraction = (key: GridFraction) => {
  const isFraction = key.includes('/')
  if (isFraction) {
    const [numerator, denominator] = key.split('/')
    return `${(Number(numerator) / Number(denominator)) * 100}%`
  }
  return `${Number(key) * 100}%`
}

const resolveSpacing = (key?: Spacing) => {
  if (!key) return undefined
  if (key === 'auto') return key
  if (key === 'none') return key
  if (isGridFraction(key)) return resolveGridFraction(key)

  return quarks.margins[key]
}

const resolveFlexDirection = (dir?: FlexDirection) => {
  if (dir === 'col') return 'column'
  if (dir === 'col-reverse') return 'column-reverse'

  return dir
}

const resolveJustification = (justification?: Justification) => {
  if (!justification) return undefined

  if (justification === 'between') return 'space-between'
  if (justification === 'around') return 'space-around'
  if (justification === 'evenly') return 'space-evenly'
  if (justification === 'end') return 'flex-end'
  if (justification === 'start') return 'flex-start'

  return justification
}

const resolveAlignment = (alignment?: Alignment) => {
  if (!alignment) return undefined

  if (alignment === 'end') return 'flex-end'
  if (alignment === 'start') return 'flex-start'

  return alignment
}

const resolveFlexWrap = (wrap?: FlexWrap) => {
  if (wrap === true) return 'wrap'
  if (wrap === false) return 'nowrap'
  if (wrap === 'reverse') return 'wrap-reverse'

  return wrap
}

const intents = Object.values(Intent)

const intentColors: { [intent in Intent]: string } = {
  none: 'initial',
  primary: Colors.BLUE3,
  success: Colors.GREEN3,
  warning: Colors.ORANGE5,
  danger: Colors.RED3,
} as const

const resolveColor = (color?: Color) => {
  if (!color) return undefined

  const semanticColor = intents.find((c) => c === color)

  if (semanticColor) {
    return intentColors[semanticColor]
  }

  return Colors[color as keyof typeof Colors]
}

const resolveBorderRadii = (borderRadius?: BorderRadius) => {
  if (!borderRadius) return undefined

  return quarks.borderRadii[borderRadius]
}

export const boxCss = css<BoxProps>(
  ({
    m,
    mx,
    my,
    mt,
    mb,
    ml,
    mr,
    p,
    px,
    py,
    pt,
    pb,
    pl,
    pr,
    width,
    minWidth,
    maxWidth,
    height,
    minHeight,
    maxHeight,
    flex,
    items,
    justify,
    flexDir,
    wrap,
    inline,
    bg,
    color,
    borderRadius,
    textAlign,
    gap,
  }) => {
    // Since flex is often combined with some other flex property,
    // we can implicitly enable it. Maybe we want to be explicit though. I don't know.
    const shouldHaveImplicitDisplayFlex =
      !!items || !!justify || !!flexDir || !!wrap

    const display = `${inline ? 'inline-' : ''}${
      shouldHaveImplicitDisplayFlex ? 'flex' : 'block'
    }`

    const gapProperties: CSSProperties =
      gap
        ? {
          gap: resolveSpacing(gap),
          }
        : {
          gap: "small"
          }

    const margins: CSSProperties =
      m && !mx && !my && !mt && !mb && !ml && !mb
        ? {
            margin: resolveSpacing(m),
          }
        : {
            marginLeft: resolveSpacing(ml ?? mx ?? m),
            marginRight: resolveSpacing(mr ?? mx ?? m),
            marginTop: resolveSpacing(mt ?? my ?? m),
            marginBottom: resolveSpacing(mb ?? my ?? m),
          }

    const paddings: CSSProperties =
      p && !px && !py && !pt && !pb && !pl && !pb
        ? {
            padding: resolveSpacing(p),
          }
        : {
            paddingLeft: resolveSpacing(pl ?? px ?? p),
            paddingRight: resolveSpacing(pr ?? px ?? p),
            paddingTop: resolveSpacing(pt ?? py ?? p),
            paddingBottom: resolveSpacing(pb ?? py ?? p),
          }

    const flexStyles: CSSProperties = {
      ...(shouldHaveImplicitDisplayFlex
        ? {
            flexDirection: resolveFlexDirection(flexDir),
            flexWrap: resolveFlexWrap(wrap),
            alignItems: resolveAlignment(items),
            justifyContent: resolveJustification(justify),
          }
        : {}),
      flex,
    }

    const widthStyles: CSSProperties = {
      width: resolveSpacing(width),
      maxWidth: resolveSpacing(maxWidth),
      minWidth: resolveSpacing(minWidth),
    }

    const heightStyles: CSSProperties = {
      height: resolveSpacing(height),
      maxHeight: resolveSpacing(maxHeight),
      minHeight: resolveSpacing(minHeight),
    }

    const colorStyles = {
      background: resolveColor(bg),
      color: resolveColor(color),
    }

    const borderRadiusStyles = {
      borderRadius: resolveBorderRadii(borderRadius),
    }

    const textAlignStyles = {
      textAlign,
    }

    return {
      display,
      ...gapProperties,
      ...margins,
      ...paddings,
      ...widthStyles,
      ...heightStyles,
      ...flexStyles,
      ...colorStyles,
      ...borderRadiusStyles,
      ...textAlignStyles,
    }
  },
)

const Box = styled.div.withConfig(
  makeExcludePropsConfig([
    'm',
    'mx',
    'my',
    'mt',
    'mb',
    'ml',
    'mr',
    'p',
    'px',
    'py',
    'pt',
    'pb',
    'pl',
    'pr',
    'width',
    'minWidth',
    'maxWidth',
    'height',
    'minHeight',
    'maxHeight',
    'flex',
    'items',
    'justify',
    'flexDir',
    'wrap',
    'inline',
    'bg',
    'color',
    'borderRadius',
    'textAlign',
    'gap',
  ]),
)<BoxProps>`
  ${boxCss}
`

Box.displayName = 'Box'

export default Box
